import React, { useState } from 'react';
import DisplayMetaWithImport from "../components/DisplayMetaWithImport.comp";
import { formatDate } from "../../../utils/utils";
import { DQProPlans } from "../../../services/metadata.service";
import { useParams } from "react-router-dom";
import { Button, Snackbar } from '@material-ui/core';
import { normalizePacProducts } from '../components/PacProductsPicker.comp';
import { normalizePacFeatures } from '../components/PacFeaturesPicker.comp';

const columns = [
    // ── Tab 1: What customers see (display) ───────────────────────────────────
    { name: 'Plan name', key: 1, json_key: 'label', data_key: 'label', section: 'display', groupLabel: 'Basic information' },
    { name: 'Short tagline', key: 2, json_key: 'subtitle', data_key: 'subtitle', section: 'display' },
    { name: 'Full description', key: 3, json_key: 'description', data_key: 'description', section: 'display' },
    { name: 'Best for (audience)', key: 4, json_key: 'best_for', data_key: 'best_for', section: 'display' },
    { name: 'Plan highlights (one per line)', key: 23, json_key: 'features', data_key: 'features_data', section: 'display' },
    { name: 'Highlight as recommended plan', key: 24, json_key: 'recommended', data_key: 'recommended', section: 'display' },
    { name: 'Price text shown to customers', key: 6, json_key: 'price_monthly_label', data_key: 'price_monthly_label', section: 'display', groupLabel: 'Pricing & credits (display text)' },
    { name: 'Credits explanation', key: 9, json_key: 'credit_system_label', data_key: 'credit_system_label', section: 'display' },
    { name: 'Credit value text', key: 10, json_key: 'credit_value_label', data_key: 'credit_value_label', section: 'display' },
    { name: 'Program creation cost text', key: 11, json_key: 'cost_program_creation_label', data_key: 'cost_program_creation_label', section: 'display' },
    { name: 'Baseline assessment cost text', key: 12, json_key: 'cost_baseline_label', data_key: 'cost_baseline_label', section: 'display' },
    { name: 'Learner program access cost text', key: 13, json_key: 'cost_user_program_access_label', data_key: 'cost_user_program_access_label', section: 'display' },
    { name: 'Learner assessment access cost text', key: 14, json_key: 'cost_user_assessment_access_label', data_key: 'cost_user_assessment_access_label', section: 'display' },
    { name: 'Sessions limit text', key: 15, json_key: 'max_sessions_per_program_label', data_key: 'max_sessions_per_program_label', section: 'display' },
    { name: 'Dashboard type label', key: 16, json_key: 'dashboard_type', data_key: 'dashboard_type', section: 'display', groupLabel: 'Other customer-facing text' },
    { name: 'Reporting label', key: 17, json_key: 'reporting_label', data_key: 'reporting_label', section: 'display' },
    { name: 'Content audience label', key: 18, json_key: 'content_audience', data_key: 'content_audience', section: 'display' },
    { name: 'Content editing label', key: 19, json_key: 'content_editing', data_key: 'content_editing', section: 'display' },
    { name: 'Integrations / API label', key: 20, json_key: 'integrations_api', data_key: 'integrations_api', section: 'display' },
    { name: 'Support level label', key: 21, json_key: 'support_level', data_key: 'support_level', section: 'display' },
    { name: 'Sign-up button text', key: 22, json_key: 'cta_label', data_key: 'cta_label', section: 'display' },
    // ── Tab 2: Pricing & rules (internal) ─────────────────────────────────────
    { name: 'Internal plan code', key: 0, json_key: 'key', data_key: 'key', section: 'internal', groupLabel: 'Plan setup' },
    { name: 'Sort order', key: 25, json_key: 'order_no', data_key: 'order_no', section: 'internal' },
    { name: 'Plan is active', key: 30, json_key: 'is_active', data_key: 'is_active', section: 'internal' },
    { name: 'Who can see this plan', key: 26, json_key: 'access', data_key: 'access', section: 'internal' },
    { name: 'Free tier plan', key: 29, json_key: 'is_free_tier', data_key: 'is_free_tier', section: 'internal' },
    { name: 'Monthly subscription price (USD)', key: 5, json_key: 'price_monthly_usd', data_key: 'price_monthly_usd', section: 'internal', groupLabel: 'Pricing & billing' },
    { name: 'Price per learner (USD)', key: 7, json_key: 'student_license_usd', data_key: 'student_license_usd', section: 'internal' },
    { name: 'Billing cycle', key: 27, json_key: 'billing_interval', data_key: 'billing_interval', section: 'internal' },
    { name: 'Free trial length (days)', key: 28, json_key: 'trial_days', data_key: 'trial_days', section: 'internal' },
    { name: 'Stripe monthly (recurring) price ID', key: 42, json_key: 'stripe_flat_price_id', data_key: 'stripe_flat_price_id', section: 'internal' },
    { name: 'Credits included each month', key: 36, json_key: 'monthly_credits_included', data_key: 'monthly_credits_included', section: 'internal', groupLabel: 'Credits & usage costs' },
    { name: 'Value of 1 credit (USD)', key: 37, json_key: 'credit_value_usd', data_key: 'credit_value_usd', section: 'internal' },
    { name: 'Unused credits roll over', key: 8, json_key: 'credits_rollover', data_key: 'credits_rollover', section: 'internal' },
    { name: 'Credits to create a program', key: 38, json_key: 'cost_program_creation_credits', data_key: 'cost_program_creation_credits', section: 'internal' },
    { name: 'Credits to create an assessment', key: 39, json_key: 'cost_assessment_creation_credits', data_key: 'cost_assessment_creation_credits', section: 'internal' },
    { name: 'Credits per learner (program access)', key: 40, json_key: 'cost_user_program_access_credits', data_key: 'cost_user_program_access_credits', section: 'internal' },
    { name: 'Credits per learner (assessment access)', key: 41, json_key: 'cost_user_assessment_access_credits', data_key: 'cost_user_assessment_access_credits', section: 'internal' },
    { name: 'Sales channel', key: 45, json_key: 'channel', data_key: 'channel', section: 'internal', groupLabel: 'DQ Token (DQT)' },
    { name: 'Solution (sign-up)', key: 51, json_key: 'solution_code', data_key: 'solution_code', section: 'internal' },
    { name: 'Solution variant (direct plans)', key: 53, json_key: 'solution_variant', data_key: 'solution_variant', section: 'internal' },
    { name: 'Learning programs for a direct buyer', key: 54, json_key: 'client_learning_access', data_key: 'client_learning_access', section: 'internal' },
    { name: 'Solution label (LearnCreator chat)', key: 52, json_key: 'solution_label', data_key: 'solution_label', section: 'internal' },
    { name: 'DQT per credit', key: 46, json_key: 'credit_price_dqt', data_key: 'credit_price_dqt', section: 'internal' },
    { name: 'DQT per licence', key: 47, json_key: 'licence_price_dqt', data_key: 'licence_price_dqt', section: 'internal' },
    { name: 'Stripe one-time plan price ID', key: 48, json_key: 'stripe_onetime_price_id', data_key: 'stripe_onetime_price_id', section: 'internal', groupLabel: 'Buying with DQ Tokens (one-time, Stripe)' },
    { name: 'Stripe top-up price ID (customer chooses amount)', key: 50, json_key: 'topup_custom_price_id', data_key: 'topup_custom_price_id', section: 'internal' },
    { name: 'Maximum programs allowed', key: 31, json_key: 'max_programs', data_key: 'max_programs', section: 'internal', groupLabel: 'Limits' },
    { name: 'Maximum sessions per program', key: 33, json_key: 'max_sessions_per_program', data_key: 'max_sessions_per_program', section: 'internal' },
    { name: 'Sessions shown in UI', key: 32, json_key: 'session_display_limit', data_key: 'session_display_limit', section: 'internal' },
    { name: 'Unlimited baseline assessments', key: 34, json_key: 'unlimited_baseline', data_key: 'unlimited_baseline', section: 'internal' },
    { name: 'Unlimited schools / orgs', key: 35, json_key: 'unlimited_schools', data_key: 'unlimited_schools', section: 'internal' },
    { name: 'Products included in plan', key: 43, json_key: 'pac_products', data_key: 'pac_products', section: 'internal', groupLabel: 'What is included in this plan' },
    { name: 'Platform features included', key: 44, json_key: 'pac_features', data_key: 'pac_features', section: 'internal' },
];

const fieldConfig = {
    key: {
        multiline: false,
        required: true,
        helperText: 'Unique code used by the system (letters, numbers, "_" or "-" only). Do not change after customers are on this plan.',
        validate: (val) => {
            const v = String(val || '').trim();
            if (!v) return 'Plan code is required';
            if (!/^[a-zA-Z0-9_-]+$/.test(v)) return 'Use letters, numbers, "_" or "-" only';
            return '';
        },
    },
    label: {
        multiline: false,
        required: true,
        helperText: 'The plan name customers see on the pricing page.',
        validate: (val) => String(val || '').trim() ? '' : 'Plan name is required',
    },
    subtitle: { multiline: false, required: false, helperText: 'Optional short line under the plan name.' },
    description: { multiline: true, rowsMax: 4, required: false, helperText: 'Longer description shown on the pricing page.' },
    price_monthly_usd: {
        multiline: false,
        required: true,
        type: 'number',
        inputProps: { min: 0, step: 0.01 },
        helperText: 'Actual monthly charge in US dollars (0 for free plans).',
        validate: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n)) return 'Enter a valid number';
            if (n < 0) return 'Price cannot be negative';
            return '';
        },
    },
    student_license_usd: {
        multiline: false,
        required: true,
        type: 'number',
        inputProps: { min: 0, step: 0.01 },
        helperText: 'Price charged per learner seat, in US dollars.',
        validate: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n)) return 'Enter a valid number';
            if (n < 0) return 'Price cannot be negative';
            return '';
        },
    },
    max_programs: {
        multiline: false,
        required: false,
        type: 'number',
        inputProps: { min: 1, step: 1 },
        helperText: 'Leave blank for unlimited programs.',
        validate: (val) => {
            if (val === '' || val === null || val === undefined) return '';
            const n = Number(val);
            if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
                return 'Enter a whole number (1 or more), or leave blank';
            }
            return '';
        },
    },
    unlimited_baseline: {
        multiline: false,
        required: true,
        selectOptions: [
            { value: true, label: 'true' },
            { value: false, label: 'false' },
        ],
        helperText: 'If true, baseline assessments are not limited on this plan.',
    },
    unlimited_schools: {
        multiline: false,
        required: true,
        selectOptions: [
            { value: true, label: 'true' },
            { value: false, label: 'false' },
        ],
        helperText: 'If true, customers can add unlimited schools or organisations.',
    },
    recommended: {
        multiline: false,
        required: true,
        selectOptions: [
            { value: true, label: 'true' },
            { value: false, label: 'false' },
        ],
        helperText: 'Only one plan can be highlighted as recommended at a time.',
    },
    access: {
        multiline: false,
        required: true,
        selectOptions: [
            { value: 'public', label: 'public' },
            { value: 'private', label: 'private' },
            { value: 'custom', label: 'custom' },
        ],
        helperText: 'Who can see and sign up for this plan.',
    },
    is_active: {
        multiline: false,
        required: true,
        selectOptions: [
            { value: true, label: 'true' },
            { value: false, label: 'false' },
        ],
        helperText: 'Inactive plans are hidden and cannot be selected.',
    },
    order_no: {
        multiline: false,
        required: true,
        type: 'number',
        inputProps: { min: 1, step: 1 },
        helperText: 'Display order on the pricing page (1 = first). Each plan needs a unique number.',
        validate: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
                return 'Enter a whole number starting from 1';
            }
            return '';
        },
    },
    features: {
        multiline: true, rowsMax: 8, required: false,
        helperText: 'Bullet points for the pricing page. Use "# Section title" for headers and "- item" for list items.',
    },

    // ── Display labels (text customers see) ───────────────────────────────────
    best_for: { multiline: false, required: false, helperText: 'e.g. "Small teams" or "Enterprise organisations".' },
    price_monthly_label: { multiline: false, required: false, helperText: 'Text shown instead of the raw price, e.g. "$49/month" or "Contact us".' },
    credits_rollover: {
        multiline: false, required: false,
        selectOptions: [{ value: true, label: 'true' }, { value: false, label: 'false' }],
        helperText: 'If true, unused credits carry over to the next month.',
    },
    credit_system_label: { multiline: false, required: false, helperText: 'How credits are described, e.g. "Pay-as-you-go credits".' },
    credit_value_label: { multiline: false, required: false, helperText: 'e.g. "1 Credit = $1"' },
    cost_program_creation_label: { multiline: false, required: false, helperText: 'Text shown for program creation cost on the pricing page.' },
    cost_baseline_label: { multiline: false, required: false, helperText: 'Text shown for baseline assessment cost.' },
    cost_user_program_access_label: { multiline: false, required: false, helperText: 'Text shown for per-learner program access cost.' },
    cost_user_assessment_access_label: { multiline: false, required: false, helperText: 'Text shown for per-learner assessment access cost.' },
    max_sessions_per_program_label: { multiline: false, required: false, helperText: 'Text shown for session limits, e.g. "Up to 10 sessions".' },
    dashboard_type: { multiline: false, required: false, helperText: 'e.g. "Standard" or "Advanced analytics".' },
    reporting_label: { multiline: false, required: false, helperText: 'e.g. "Basic reports" or "Full analytics suite".' },
    content_audience: { multiline: false, required: false, helperText: 'Who the content is designed for, e.g. "K-12 students".' },
    content_editing: { multiline: false, required: false, helperText: 'e.g. "View only" or "Full editing".' },
    integrations_api: { multiline: false, required: false, helperText: 'Leave blank for none, or describe e.g. "API access included".' },
    support_level: { multiline: false, required: false, helperText: 'e.g. "Email support" or "Dedicated account manager".' },
    cta_label: { multiline: false, required: false, helperText: 'Sign-up button text, e.g. "Get Started" or "Contact Sales".' },
    billing_interval: {
        multiline: false, required: false,
        selectOptions: [
            { value: 'monthly', label: 'monthly' },
            { value: 'yearly', label: 'yearly' },
            { value: 'custom', label: 'custom' },
        ],
        helperText: 'How often the customer is billed.',
    },
    trial_days: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Free trial length in days. Enter 0 for no trial.',
    },
    is_free_tier: {
        multiline: false, required: false,
        selectOptions: [{ value: true, label: 'true' }, { value: false, label: 'false' }],
        helperText: 'Mark as true for free starter plans (no payment required).',
    },

    // ── Limits ────────────────────────────────────────────────────────────────
    session_display_limit: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 1, step: 1 },
        helperText: 'How many sessions to show in the UI. Leave blank for unlimited.',
    },
    max_sessions_per_program: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 1, step: 1 },
        helperText: 'Maximum sessions allowed per program. Leave blank for unlimited.',
        validate: (val) => {
            if (val === '' || val === null || val === undefined) return '';
            const n = Number(val);
            if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return 'Enter a whole number or leave blank';
            return '';
        },
    },

    // ── Credits / Internal ────────────────────────────────────────────────────
    monthly_credits_included: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Credits automatically added each billing cycle.',
    },
    credit_value_usd: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 0.01 },
        helperText: 'Dollar value of 1 credit. Usually 1. Leave blank for custom contracts.',
    },
    cost_program_creation_credits: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Credits deducted when a program is created. Leave blank for custom/enterprise.',
    },
    cost_assessment_creation_credits: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Credits deducted when an assessment is created. Leave blank for custom/enterprise.',
    },
    cost_user_program_access_credits: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Credits per learner for program access. Leave blank for custom/enterprise.',
    },
    cost_user_assessment_access_credits: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 1 },
        helperText: 'Credits per learner for assessment access. Leave blank for custom/enterprise.',
    },

    // ── DQ Token (DQT) ────────────────────────────────────────────────────────
    channel: {
        multiline: false,
        required: false,
        selectOptions: [
            { value: 'partner', label: 'partner' },
            { value: 'direct', label: 'direct' },
        ],
        helperText: 'partner: a DQ Pro partner, holds DQ Tokens and assigns to its schools. direct: a network or school buying for itself — never holds DQ Tokens.',
    },
    solution_code: {
        multiline: false,
        required: false,
        selectOptions: [
            { value: '', label: '— none —' },
            { value: 'P0', label: 'P0 — COS Children Assessment' },
            { value: 'P1', label: 'P1 — School COS Readiness' },
            { value: 'LC', label: 'LC — LearnCreator' },
            { value: 'P2', label: 'P2 — Workforce AI Readiness (Measure, and Measure + Learn)' },
            { value: 'P3', label: 'P3 — Program Validation' },
        ],
        helperText: 'The sign-up solution this plan is sold for (/powered-by-dq/chatbot). A direct order uses the direct-channel plan with this code; a DQ Pro applicant pays the partner plan with this code.',
    },
    solution_variant: {
        multiline: false,
        required: false,
        selectOptions: [
            { value: '', label: '— none —' },
            { value: 'P1B', label: 'P1B — School COS Readiness, creating programs (the chat\'s P1)' },
            { value: 'P2A', label: 'P2A — Workforce AI Readiness (Measure)' },
            { value: 'P2B', label: 'P2B — Workforce AI Readiness (Measure + Learn)' },
        ],
        helperText: 'Direct plans only. A direct order goes to the direct plan of its solution AND variant: the chat\'s P1 → P1B, P2A → P2A, P2B → P2B. Leave empty for a solution with one direct plan (P0, LC, P3) and on every partner plan — a partner chooses P1A / P1B per school.',
    },
    client_learning_access: {
        multiline: false,
        required: false,
        selectOptions: [
            { value: '', label: '— LearnLinker (default) —' },
            { value: 'learn_creator', label: 'LearnCreator — view, link and create programs' },
            { value: 'learn_linker', label: 'LearnLinker — view and link programs' },
            { value: 'learn_viewer', label: 'LearnViewer — view programs' },
        ],
        helperText: 'Direct plans only: the Learning Program access the buyer\'s own school or network gets. LearnCreator for plans sold as authoring (P1B, LC, P2B). Applied when the plan is activated on a workspace, including a change from another direct plan.',
    },
    solution_label: {
        multiline: false, required: false,
        helperText: 'Solution text given to the LearnCreator program-creation chat and the ML engine for every program in a workspace on this plan, e.g. "LearnCreator". Leave blank to use the name of the solution selected above, or "Digital Literacy" when none is selected.',
        validate: (val) => (String(val || '').length > 120 ? 'Keep it under 120 characters' : ''),
    },
    credit_price_dqt: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 0.01 },
        helperText: 'DQ Tokens per credit: what a partner pays when it assigns credits or builds, and — on a direct plan — the rate its top-ups and grants are converted into credits at. Leave blank to switch this off; enter 0 to make it free.',
    },
    licence_price_dqt: {
        multiline: false, required: false, type: 'number',
        inputProps: { min: 0, step: 0.01 },
        helperText: 'DQ Tokens per learner licence. Stored for the licence release; not used yet.',
    },

    // ── Stripe ────────────────────────────────────────────────────────────────
    stripe_flat_price_id: {
        multiline: false, required: false,
        helperText: 'RECURRING monthly Stripe Price (starts with price_) — subscriptions only. Never put a one-time price here; use the one-time plan price below.',
    },
    stripe_onetime_price_id: {
        multiline: false, required: false,
        helperText: 'USD ONE-TIME Stripe Price for buying this plan once (starts with price_). Buying it activates the plan and grants its price in DQ Tokens at the token rate. Leave blank and the plan is not sold online.',
        validate: (val) => {
            const v = String(val || '').trim();
            if (v && !v.startsWith('price_')) return 'A Stripe Price ID starts with price_';
            return '';
        },
    },
    topup_custom_price_id: {
        multiline: false, required: false,
        helperText: 'USD ONE-TIME Stripe Price set to "Customer chooses price" (starts with price_). This is how every top-up is sold — there are no fixed packs: owners type any USD amount within the price\'s minimum and maximum (or pick one of the portal\'s quick amounts) and get it in DQ Tokens at the token rate; a direct plan gets credits, and buys licences on this price too. Leave blank for no top-ups.',
        validate: (val) => {
            const v = String(val || '').trim();
            if (v && !v.startsWith('price_')) return 'A Stripe Price ID starts with price_';
            return '';
        },
    },

    // ── PAC v3 (package model) ──────────────────────────────────────────────────
    pac_products: {
        fieldType: 'pac_products_picker',
        required: false,
        emptyDefault: {},
        helperText: 'Choose which products (e.g. assessments, learning programs) are included in this plan.',
    },
    pac_features: {
        fieldType: 'pac_features_picker',
        required: false,
        emptyDefault: {},
        helperText: 'Choose which platform features (e.g. white-label, analytics) are included in this plan.',
    },
};

const tableCols = [
    { label: 'Key', field: 'key', sort: 'asc', width: 140 },
    { label: 'Label', field: 'label', sort: 'asc', width: 140 },
    { label: 'Monthly (USD)', field: 'price_monthly_usd', sort: 'asc', width: 100 },
    { label: 'Per Student', field: 'student_license_usd', sort: 'asc', width: 90 },
    { label: 'Credits/mo', field: 'monthly_credits_included', sort: 'asc', width: 90 },
    { label: 'Max Programs', field: 'max_programs', sort: 'asc', width: 100 },
    { label: 'Billing', field: 'billing_interval', sort: 'asc', width: 80 },
    { label: 'Trial Days', field: 'trial_days', sort: 'asc', width: 80 },
    { label: 'Access', field: 'access', sort: 'asc', width: 90 },
    { label: 'Active', field: 'is_active', sort: 'asc', width: 70 },
    { label: 'Stripe Flat ID', field: 'stripe_flat_price_id', sort: 'disabled', width: 200 },
    { label: 'Features', field: 'features', sort: 'disabled', width: 220 },
    { label: 'Created', field: 'createdAt', width: 150 },
    { label: 'Updated', field: 'updatedAt', width: 150 },
    { label: 'Actions', field: 'actions', sort: 'disabled', searchable: false },
];

const normalizeFeatures = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val !== 'string') return [];
    return val.split('\n').map(x => x.trim()).filter(Boolean);
};

const additional_json_rendering = (json_obj) => {
    json_obj.features = normalizeFeatures(json_obj.features);
    json_obj.pac_products = normalizePacProducts(json_obj.pac_products);
    json_obj.pac_features = normalizePacFeatures(json_obj.pac_features);
    return json_obj;
};

const table_row__additional_rendering = (row) => {
    const features = normalizeFeatures(row.features);
    row.features_data = features.join('\n');
    row.features = features.length > 0
        ? features.map((f, i) => <span key={i} className={'meta-data-table-array-span'}>{f}</span>)
        : '-';
    row.pac_products = normalizePacProducts(row.pac_products);
    row.pac_features = normalizePacFeatures(row.pac_features);
    row.createdAt = `${formatDate(row.createdAt)},\n ${row.createdBy ? row.createdBy : ''}`;
    row.updatedAt = `${formatDate(row.updatedAt)},\n ${row.updatedBy ? row.updatedBy : ''}`;
    return row;
};

const DQProPlansData = () => {
    // url_param_str is only present on the standalone route (/meta_data/dq_pro_plans/:url_param_str).
    // When this component is embedded (e.g. as a tab in Licenses & Billing) there is no route
    // param, so default it safely to avoid a crash on .split().
    const { url_param_str = '' } = useParams();
    const search_query = (url_param_str && url_param_str.split('=').length > 1) ? url_param_str.split('=')[1] : '';
    const [clearing, setClearing] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');

    const handleClearCache = async () => {
        setClearing(true);
        try {
            await DQProPlans.clearCache();
            setSnackMsg('Cache cleared and rebuilt successfully.');
        } catch (_) {
            setSnackMsg('Failed to clear cache.');
        } finally {
            setClearing(false);
        }
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px' }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    disabled={clearing}
                    onClick={handleClearCache}
                >
                    {clearing ? 'Clearing...' : 'Clear Cache'}
                </Button>
            </div>
            <Snackbar
                open={!!snackMsg}
                message={snackMsg}
                autoHideDuration={3000}
                onClose={() => setSnackMsg('')}
            />
        <DisplayMetaWithImport
            title={'Subscription Plans'}
            modal_title={'Subscription Plan — Add / Edit'}
            sheet_name={''}
            columns={columns}
            tableCols={tableCols}
            jsonSpRender={additional_json_rendering}
            tableSpRender={table_row__additional_rendering}
            search_query={search_query}
            listAPI={DQProPlans.listData}
            postAPI={DQProPlans.postData}
            deleteAPI={DQProPlans.deleteEntity}
            updateAPI={DQProPlans.updateEntity}
            preRemoveAPI={DQProPlans.preRemoveCheck}
            hideImportData={true}
            enableManualCreate={true}
            fieldConfig={fieldConfig}
            editModalConfig={{
                width: '70%',
                height: '75%',
                maxWidth: '960px',
                stickyFooter: true,
                saveLabel: 'Save plan',
                sectionTabLabels: {
                    display: 'What clients see',
                    internal: 'Pricing & settings',
                },
                sectionIntros: {
                    display: 'Text and labels shown on the public pricing page. You can leave most fields blank and use defaults.',
                    internal: 'Billing amounts, credit costs, limits, and what is included in this plan. Changes here affect how the plan works, not just how it looks.',
                },
            }}
        />
        </>
    );
};

export default DQProPlansData;
